import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import moment from "moment";
import { DateRangePicker } from "react-dates";
import { Row, Col } from "react-bootstrap";
import { orderBy } from "lodash";
import { startOfYear, startOfDay, format } from "date-fns";

import DropdownSearch from "../../../../common/DropdownSearch";

import * as customerActions from "../../../../../actions/customerActions";
import * as projectActions from "../../../../../actions/projectActions";
import * as codeActions from "../../../../../actions/codeActions";

import ReportFilters from "../../../ReportFiltersComponent";

const ProjectServiceAnalysisFilters = ({
  onFiltersChange,
  customers,
  projects,
  codes,
  actions,
  loading,
  onPDFExport,
  exportDisabled
}) => {
  const [filters, setFilters] = useState({
    startDate: startOfYear(new Date()),
    endDate: startOfDay(new Date()),
    customer: null,
    project: [],
    callStatus: [],
    printDisplay: 3
  });
  const [focusedInput, setFocusedInput] = useState(null);

  useEffect(() => {
    actions.loadCustomers();
    actions.loadCodes();
  }, [actions]);

  useEffect(() => {
    if (filters.customer) {
      actions.loadProjects({ customer: filters.customer });
    }
  }, [actions, filters.customer]);

  const handleChangeDate = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      startDate: startDate.toDate(),
      endDate: endDate.toDate()
    }));
  };

  const handleFiltersChange = (name, data) => {
    setFilters((prev) => ({
      ...prev,
      [name]: Array.isArray(data) ? data : data.value,
      project:
        // eslint-disable-next-line no-nested-ternary
        name === "project" ? data : name === "customer" ? [] : prev.project
    }));
  };

  const handleResetFilters = () => {
    const newFilters = {
      startDate: startOfYear(new Date()),
      endDate: startOfDay(new Date()),
      customer: null,
      project: [],
      callStatus: [],
      printDisplay: 3
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleFilterClick = () => {
    onFiltersChange(filters);
  };

  const customerOptions = useMemo(() => {
    const options = customers.map((customer) => ({
      value: customer._id,
      label: customer.name
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [customers]);

  const projectOptions = useMemo(() => {
    const options = projects.map((project) => ({
      value: project._id,
      label: project.name
    }));
    return filters.customer
      ? orderBy(options, [(option) => option.label.toLowerCase()])
      : [];
  }, [projects, filters.customer]);

  const callStatusOptions = useMemo(() => {
    const options = codes
      .filter((code) => code.entity === "Call" && code.field === "status")
      .map((code) => ({
        value: code._id,
        label: code.label
      }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [codes]);

  return (
    <ReportFilters
      onFetchClick={handleFilterClick}
      onResetClick={handleResetFilters}
      title={`Project Service Analysis Report for ${format(
        filters.startDate,
        "MM/dd/yyyy"
      )} to ${format(filters.endDate, "MM/dd/yyyy")}`}
      loading={loading}
      pdfExportAvailable
      onPDFExport={() => onPDFExport(filters)}
      exportDisabled={exportDisabled}
      disabled={!filters.customer || !filters.printDisplay}
    >
      <Row>
        <Col md={3}>
          <DropdownSearch
            name="printDisplay"
            label="Print Display *"
            placeholder="Select"
            onChange={handleFiltersChange}
            value={filters.printDisplay}
            data={[
              { value: 1, label: "List and sub-total" },
              { value: 2, label: "List only" },
              { value: 3, label: "Summary by project and service" },
              { value: 4, label: "Summary by project" }
            ]}
          />
        </Col>
        <Col md={3}>
          <DropdownSearch
            label="Customer *"
            name="customer"
            placeholder="Select"
            onChange={handleFiltersChange}
            value={filters.customer}
            data={customerOptions}
          />
        </Col>
        <Col md={3}>
          <DropdownSearch
            isMulti
            name="project"
            label="Project"
            placeholder="Select"
            onChange={handleFiltersChange}
            value={filters.project}
            data={projectOptions}
          />
        </Col>
        <Col md={3}>
          <DropdownSearch
            isMulti
            name="callStatus"
            label="Call Status"
            placeholder="Select"
            onChange={handleFiltersChange}
            value={filters.callStatus}
            data={callStatusOptions}
          />
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <label
            className="control-label"
            style={{ textTransform: "uppercase" }}
          >
            Date Range
          </label>
          <DateRangePicker
            startDate={moment(filters.startDate)}
            startDateId="startDate"
            endDate={moment(filters.endDate)}
            endDateId="endDate"
            onDatesChange={({ startDate, endDate }) => {
              handleChangeDate(startDate, endDate);
            }}
            isOutsideRange={() => false}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
          />
        </Col>
      </Row>
    </ReportFilters>
  );
};

ProjectServiceAnalysisFilters.propTypes = {
  onFiltersChange: PropTypes.func,
  customers: PropTypes.array,
  projects: PropTypes.array,
  codes: PropTypes.array,
  actions: PropTypes.object,
  loading: PropTypes.bool,
  onPDFExport: PropTypes.func,
  exportDisabled: PropTypes.bool
};

function mapStatesToProps(state) {
  return {
    auth: state.reducers.auth,
    customers: state.reducers.customers,
    projects: state.reducers.projects,
    codes: state.reducers.codes,
    loading: state.reducers.loading,
    exporting: state.reducers.exporting
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...customerActions,
        ...projectActions,
        ...codeActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(ProjectServiceAnalysisFilters);
